<template>
  <BaseReport
    ref="baseReport"
    :loading="loading"
    :reportTitle="reportTitle"
    :reportSubtitle="dateRangeLabel"
    :columns="6"
    @mounted="onMounted"
  >
    <template #controls>
      <div class="report-controls">
        <AnalyticsDateRangePicker
          :modelValue="{ startDate: rangeSelection.startDate, endDate: rangeSelection.endDate }"
          @update:modelValue="handleDateRangeChange"
        />
        <AnalyticsPeriodPicker
          v-model="rangeSelection.intervalType"
          @update:modelValue="handlePeriodChange"
        />
        <ProductSelectInput
          :modelValue="selectedProductId"
          @update:modelValue="handleProductChange"
          :allowDeleted="true"
          placeholder="Select product..."
          class="product-select"
        />
      </div>
    </template>

    <template #header v-if="data?.product">
      <div class="report-header-with-icon">
        <IconCircle
          :size="48"
          bgColor="var(--icon-product-bg)"
          color="var(--icon-product-color)"
          :icon="getProductIcon(data.product.productType)"
        />
        <div class="report-header-text">
          <h2>{{ data.product.productName }}</h2>
          <p>All metrics reflect tickets created in the selected date range. Click charts to drill down to individual tickets.</p>
        </div>
      </div>
    </template>

    <template #content>
      <!-- Empty state when no product selected -->
      <div v-if="!selectedProductId" class="empty-state">
        <p>Select a product to view its detailed performance</p>
      </div>

      <!-- Report content when product is selected -->
      <template v-else-if="data">
        <!-- Row 1: Summary KPIs -->
        <CardKpi
          class="cell cell-chart"
          title="Total Tickets"
          :value="totalTicketsKpi.value"
          format="number"
          :description="totalTicketsKpi.description"
        />
        <CardKpi
          class="cell cell-chart"
          title="Total Sales"
          :value="totalSalesKpi.value"
          format="currency"
          :description="totalSalesKpi.description"
        />
        <CardKpi
          class="cell cell-chart"
          title="Total Collected"
          :value="totalCollectedKpi.value"
          format="currency"
          :description="totalCollectedKpi.description"
        />

        <!-- Row 2: Distribution Charts -->
        <CardBarChart
          class="cell cell-half"
          title="Tickets by Cost"
          subtitle="NP vs EP by price range"
          :data="priceBucketsChartData"
          :options="priceBucketsChartOptions"
          variant="stacked"
          :horizontal="true"
          :height="300"
          @bar-click="handlePriceBucketsBarClick"
        />
        <CardBarChart
          class="cell cell-half"
          title="Top Salespeople"
          subtitle="Revenue by counselor, NP vs EP"
          :data="topCounselorsChartData"
          :options="topCounselorsChartOptions"
          variant="stacked"
          :horizontal="true"
          :height="300"
          @bar-click="handleTopCounselorsBarClick"
        />

        <!-- Row 3: Sales by Patient Type -->
        <CardLineChart
          class="cell cell-full-width"
          title="Sales by Patient Type"
          subtitle="NP vs EP over time"
          variant="stacked"
          :data="patientTypeTrendData"
          :options="lineChartOptions"
          :formatTooltipValue="formatCurrencyValue"
          :height="300"
          @point-click="handlePatientTypeChartClick"
        />

        <!-- Row 4: Sales by Counselor -->
        <CardLineChart
          class="cell cell-full-width"
          title="Sales by Counselor"
          subtitle="Per counselor over time"
          variant="stacked"
          :data="counselorTrendData"
          :options="lineChartOptions"
          :formatTooltipValue="formatCurrencyValue"
          :height="300"
          @point-click="handleCounselorChartClick"
        />

        <!-- Row 5: Counselor Sales Table -->
        <CardTable
          class="cell cell-full-width cell-data-table"
          title="Counselor Sales by Period"
          subtitle="Revenue per counselor per period"
          :columns="counselorSalesTableColumns"
          :rows="counselorSalesTableRows"
          variant="summary"
          :summaryRow="counselorSalesTableSummary"
          :stickyFirstColumn="true"
        />
      </template>
    </template>
  </BaseReport>
</template>

<script>
import BaseReport from '@/components/common/BaseReport.vue';
import CardBarChart from '@/components/common/CardBarChart.vue';
import CardLineChart from '@/components/common/CardLineChart.vue';
import CardKpi from '@/components/common/CardKpi.vue';
import CardTable from '@/components/common/CardTable.vue';
import IconCircle from '@/components/common/IconCircle.vue';
import AnalyticsDateRangePicker from '@/components/inputs/AnalyticsDateRangePicker.vue';
import AnalyticsPeriodPicker from '@/components/inputs/AnalyticsPeriodPicker.vue';
import ProductSelectInput from '@/components/inputs/ProductSelectInput.vue';
import PillIcon from 'vue-material-design-icons/Pill.vue';
import CurrencyUsdIcon from 'vue-material-design-icons/CurrencyUsd.vue';
import PackageIcon from 'vue-material-design-icons/Package.vue';
import * as api from '@/api';
import { alertService } from '@/libs/alert';
import { serializeFilters } from '@/utils/filterSerializer';
import { formatDateShort } from '@/utils/formatters';

export default {
  name: 'ProductDetailsReport',
  components: {
    BaseReport,
    CardBarChart,
    CardLineChart,
    CardKpi,
    CardTable,
    IconCircle,
    AnalyticsDateRangePicker,
    AnalyticsPeriodPicker,
    ProductSelectInput
  },
  emits: ['loading'],
  data() {
    return {
      loading: true,
      rangeSelection: {
        startDate: null,
        endDate: null,
        intervalType: 'weekly'
      },
      selectedProductId: null,
      data: null
    };
  },
  computed: {
    reportTitle() {
      return 'Product Details';
    },
    dateRangeLabel() {
      if (!this.rangeSelection.startDate || !this.rangeSelection.endDate) {
        return '';
      }
      return `${formatDateShort(this.rangeSelection.startDate)} - ${formatDateShort(this.rangeSelection.endDate)}`;
    },
    chartHeight() {
      return 300;
    },

    // Color schemes
    patientTypeColors() {
      return {
        newPatient: '#818cf8',      // indigo-400 (lighter)
        existingPatient: '#4338ca'  // indigo-700 (darker)
      };
    },

    // Total Tickets KPI
    totalTicketsKpi() {
      const summary = this.data?.summary;
      if (!summary) return { value: 0, description: '' };

      return {
        value: summary.totalTickets,
        description: `${summary.npTickets} new patient and ${summary.epTickets} existing patient tickets in this period`
      };
    },

    // Total Sales KPI
    totalSalesKpi() {
      const summary = this.data?.summary;
      if (!summary) return { value: 0, description: '' };

      return {
        value: summary.totalSalesCents / 100,
        description: `${this.formatCurrencyShort(summary.npSalesCents / 100)} from new patients, ${this.formatCurrencyShort(summary.epSalesCents / 100)} from existing patients`
      };
    },

    // Total Collected KPI
    totalCollectedKpi() {
      const summary = this.data?.summary;
      if (!summary) return { value: 0, description: '' };

      return {
        value: summary.totalCollectedCents / 100,
        description: `${this.formatCurrencyShort(summary.npCollectedCents / 100)} from new patients, ${this.formatCurrencyShort(summary.epCollectedCents / 100)} from existing patients`
      };
    },

    // Price Buckets Chart Data
    priceBucketsChartData() {
      if (!this.data?.summary?.priceBuckets) return null;

      const buckets = this.data.summary.priceBuckets;
      const nonEmptyBuckets = buckets.filter(b => b.npTickets > 0 || b.epTickets > 0);

      if (nonEmptyBuckets.length === 0) return null;

      return {
        labels: nonEmptyBuckets.map(b => b.label),
        datasets: [
          {
            label: 'Existing Patient',
            data: nonEmptyBuckets.map(b => b.epTickets),
            color: this.patientTypeColors.existingPatient
          },
          {
            label: 'New Patient',
            data: nonEmptyBuckets.map(b => b.npTickets),
            color: this.patientTypeColors.newPatient
          }
        ]
      };
    },
    priceBucketsChartOptions() {
      return {
        tooltip: {
          formatter: (params) => {
            if (!Array.isArray(params)) params = [params];

            let total = 0;
            let html = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`;

            params.forEach(param => {
              const value = param.value || 0;
              total += value;
              const color = param.color;
              html += `<div style="display: flex; align-items: center; margin-bottom: 4px;">`;
              html += `<span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${color}; margin-right: 8px;"></span>`;
              html += `<span style="flex: 1;">${param.seriesName}:</span>`;
              html += `<span style="font-weight: 600; margin-left: 12px;">${value} tickets</span>`;
              html += `</div>`;
            });

            html += `<div style="border-top: 1px solid rgba(255,255,255,0.2); margin-top: 8px; padding-top: 8px; font-weight: 600;">`;
            html += `<span>Total:</span>`;
            html += `<span style="float: right; margin-left: 12px;">${total} tickets</span>`;
            html += `</div>`;

            return html;
          }
        }
      };
    },

    // Top Counselors Chart Data
    topCounselorsChartData() {
      if (!this.data?.summary?.topCounselors) return null;

      const counselors = this.data.summary.topCounselors;
      if (counselors.length === 0) return null;

      // Take top 7 counselors, aggregate rest into "Other"
      const topCounselors = counselors.slice(0, 7);
      const otherCounselors = counselors.slice(7);

      // Build data array with top counselors
      const chartData = [...topCounselors];

      // Add "Other" row if there are more counselors
      if (otherCounselors.length > 0) {
        const otherEpSales = otherCounselors.reduce((sum, c) => sum + (c.epSalesCents || 0), 0);
        const otherNpSales = otherCounselors.reduce((sum, c) => sum + (c.npSalesCents || 0), 0);
        if (otherEpSales > 0 || otherNpSales > 0) {
          chartData.push({
            counselorName: 'Other',
            epSalesCents: otherEpSales,
            npSalesCents: otherNpSales
          });
        }
      }

      // Reverse so highest is at top in horizontal bar
      const reversed = chartData.reverse();

      return {
        labels: reversed.map(c => c.counselorName),
        datasets: [
          {
            label: 'Existing Patient',
            data: reversed.map(c => c.epSalesCents / 100),
            color: this.patientTypeColors.existingPatient
          },
          {
            label: 'New Patient',
            data: reversed.map(c => c.npSalesCents / 100),
            color: this.patientTypeColors.newPatient
          }
        ]
      };
    },
    topCounselorsChartOptions() {
      return {
        tooltip: {
          formatter: (params) => {
            if (!Array.isArray(params)) params = [params];

            let total = 0;
            let html = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`;

            params.forEach(param => {
              const value = param.value || 0;
              total += value;
              const color = param.color;
              html += `<div style="display: flex; align-items: center; margin-bottom: 4px;">`;
              html += `<span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${color}; margin-right: 8px;"></span>`;
              html += `<span style="flex: 1;">${param.seriesName}:</span>`;
              html += `<span style="font-weight: 600; margin-left: 12px;">${this.formatCurrencyValue(value)}</span>`;
              html += `</div>`;
            });

            html += `<div style="border-top: 1px solid rgba(255,255,255,0.2); margin-top: 8px; padding-top: 8px; font-weight: 600;">`;
            html += `<span>Total:</span>`;
            html += `<span style="float: right; margin-left: 12px;">${this.formatCurrencyValue(total)}</span>`;
            html += `</div>`;

            return html;
          }
        }
      };
    },

    // Patient Type Trend Data
    patientTypeTrendData() {
      if (!this.data?.salesByPatientType) return null;

      const { periods, existingPatientSales, newPatientSales } = this.data.salesByPatientType;

      return {
        labels: periods,
        datasets: [
          {
            label: 'Existing Patient',
            data: existingPatientSales.map(v => v / 100),
            color: this.patientTypeColors.existingPatient
          },
          {
            label: 'New Patient',
            data: newPatientSales.map(v => v / 100),
            color: this.patientTypeColors.newPatient
          }
        ]
      };
    },

    // Counselor Trend Data
    counselorTrendData() {
      if (!this.data?.salesByCounselor?.counselors) return null;

      const { periods, counselors } = this.data.salesByCounselor;

      // Take top 7, group rest under "Other"
      const sortedCounselors = [...counselors].sort((a, b) => b.totalSalesCents - a.totalSalesCents);
      const topCounselors = sortedCounselors.slice(0, 7);
      const otherCounselors = sortedCounselors.slice(7);

      // Calculate "Other" totals per period
      let otherData = null;
      if (otherCounselors.length > 0) {
        const otherSalesPerPeriod = periods.map((_, periodIndex) => {
          return otherCounselors.reduce((sum, c) => sum + (c.salesPerPeriod[periodIndex] || 0), 0);
        });
        const otherTotal = otherSalesPerPeriod.reduce((sum, v) => sum + v, 0);
        if (otherTotal > 0) {
          otherData = {
            label: 'Other',
            data: otherSalesPerPeriod.map(v => v / 100),
            color: '#e0e7ff' // indigo-100 (lightest)
          };
        }
      }

      // Reverse so highest is on top
      const colors = this.generateIndigoShades(topCounselors.length);
      const datasets = topCounselors.reverse().map((counselor, index) => ({
        label: counselor.counselorName,
        data: counselor.salesPerPeriod.map(v => v / 100),
        color: colors[topCounselors.length - 1 - index]
      }));

      if (otherData) {
        datasets.unshift(otherData);
      }

      return {
        labels: periods,
        datasets
      };
    },

    lineChartOptions() {
      return {
        yAxis: {
          axisLabel: {
            formatter: (value) => this.formatCurrencyShort(value)
          }
        }
      };
    },

    // Counselor Sales Table
    counselorSalesTableColumns() {
      if (!this.data?.salesByCounselor?.periods) return [];

      const periods = this.data.salesByCounselor.periods;
      return [
        { key: 'counselor', label: 'Counselor' },
        ...periods.map((period, index) => ({
          key: `period_${index}`,
          label: period,
          format: 'currency'
        })),
        { key: 'total', label: 'Total', format: 'currency' }
      ];
    },
    counselorSalesTableRows() {
      if (!this.data?.salesByCounselor?.counselors) return [];

      const { counselors } = this.data.salesByCounselor;

      return counselors.map(counselor => {
        const row = {
          counselor: counselor.counselorName,
          total: counselor.totalSalesCents / 100
        };
        counselor.salesPerPeriod.forEach((sales, index) => {
          row[`period_${index}`] = sales / 100;
        });
        return row;
      });
    },
    counselorSalesTableSummary() {
      if (!this.counselorSalesTableRows.length) return null;

      const summary = { counselor: 'Total', total: 0 };
      const periodCount = this.data?.salesByCounselor?.periods?.length || 0;

      for (let i = 0; i < periodCount; i++) {
        summary[`period_${i}`] = this.counselorSalesTableRows.reduce((sum, row) => sum + (row[`period_${i}`] || 0), 0);
      }
      summary.total = this.counselorSalesTableRows.reduce((sum, row) => sum + (row.total || 0), 0);

      return summary;
    }
  },
  methods: {
    generateIndigoShades(count) {
      // Generate different shades of indigo from light to dark (matches FinancialsReport)
      const indigoShades = [
        '#c7d2fe', // indigo-200
        '#a5b4fc', // indigo-300
        '#818cf8', // indigo-400
        '#6366f1', // indigo-500
        '#4f46e5', // indigo-600
        '#4338ca', // indigo-700
        '#3730a3', // indigo-800
        '#312e81'  // indigo-900
      ];

      if (count > indigoShades.length) {
        const result = [];
        for (let i = 0; i < count; i++) {
          result.push(indigoShades[i % indigoShades.length]);
        }
        return result;
      }

      return indigoShades.slice(0, count);
    },

    async onMounted() {
      // Initialize from URL query params if present
      const query = this.$route.query;
      if (query.startDate) {
        this.rangeSelection.startDate = query.startDate;
      }
      if (query.endDate) {
        this.rangeSelection.endDate = query.endDate;
      }
      if (query.intervalType) {
        this.rangeSelection.intervalType = query.intervalType;
      }
      if (query.productId) {
        this.selectedProductId = parseInt(query.productId, 10);
      }

      // Load data if all required params are present
      if (this.rangeSelection.startDate && this.rangeSelection.endDate && this.selectedProductId) {
        await this.loadData();
      }

      this.$emit('loading', false);
      this.loading = false;
    },

    handleDateRangeChange(dateRange) {
      this.rangeSelection.startDate = dateRange.startDate;
      this.rangeSelection.endDate = dateRange.endDate;
      if (this.rangeSelection.startDate && this.rangeSelection.endDate && this.selectedProductId) {
        this.loadData();
      }
    },

    handlePeriodChange() {
      if (this.rangeSelection.startDate && this.rangeSelection.endDate && this.selectedProductId) {
        this.loadData();
      }
    },

    handleProductChange(productId) {
      this.selectedProductId = productId;
      if (this.rangeSelection.startDate && this.rangeSelection.endDate && productId) {
        this.loadData();
      } else {
        this.data = null;
      }
    },

    async loadData() {
      if (!this.rangeSelection.startDate || !this.rangeSelection.endDate || !this.selectedProductId) {
        return;
      }

      this.loading = true;

      try {
        const result = await api.fetchProductDetails({
          startDate: this.rangeSelection.startDate,
          endDate: this.rangeSelection.endDate,
          intervalType: this.rangeSelection.intervalType,
          productId: this.selectedProductId
        });

        this.data = result;
      } catch (err) {
        console.error('Failed to load product details:', err);
        alertService.error('Failed to load product details: ' + err.message);
        this.data = null;
      } finally {
        this.loading = false;
      }
    },

    handlePatientTypeChartClick(event) {
      const { dataIndex, seriesName } = event;
      const periodDates = this.data?.salesByPatientType?.periodDates;
      if (!periodDates || !periodDates[dataIndex]) return;

      const period = periodDates[dataIndex];

      const filters = [
        {
          field: 'dateTicketStart',
          operator: 'between',
          from: period.start,
          to: period.end
        }
      ];

      // Add productId filter
      if (this.selectedProductId) {
        filters.push({
          field: 'productIds',
          operator: 'any',
          values: [this.selectedProductId]
        });
      }

      // Add ticketType filter based on which series was clicked
      if (seriesName === 'New Patient') {
        filters.push({
          field: 'ticketType',
          operator: 'is',
          value: 'new_patient'
        });
      } else if (seriesName === 'Existing Patient') {
        filters.push({
          field: 'ticketType',
          operator: 'is',
          value: 'existing_patient'
        });
      }

      const serializedFilters = serializeFilters(filters);
      this.$router.push({
        path: '/tickets',
        query: { filters: serializedFilters }
      });
    },

    handleCounselorChartClick(event) {
      const { dataIndex, seriesName } = event;
      const periodDates = this.data?.salesByCounselor?.periodDates;
      if (!periodDates || !periodDates[dataIndex]) return;

      const period = periodDates[dataIndex];
      const counselor = this.data.salesByCounselor.counselors.find(c => c.counselorName === seriesName);

      const filters = [
        {
          field: 'dateTicketStart',
          operator: 'between',
          from: period.start,
          to: period.end
        }
      ];

      // Add productId filter
      if (this.selectedProductId) {
        filters.push({
          field: 'productIds',
          operator: 'any',
          values: [this.selectedProductId]
        });
      }

      if (counselor) {
        filters.push({
          field: 'counselorId',
          operator: 'is',
          value: counselor.counselorId
        });
      }

      const serializedFilters = serializeFilters(filters);
      this.$router.push({
        path: '/tickets',
        query: { filters: serializedFilters }
      });
    },

    handlePriceBucketsBarClick(event) {
      const { name } = event;

      const filters = [];

      // Add date range filter if available
      if (this.rangeSelection.startDate && this.rangeSelection.endDate) {
        filters.push({
          field: 'dateTicketStart',
          operator: 'between',
          from: this.rangeSelection.startDate,
          to: this.rangeSelection.endDate
        });
      }

      // Add productId filter
      if (this.selectedProductId) {
        filters.push({
          field: 'productIds',
          operator: 'any',
          values: [this.selectedProductId]
        });
      }

      // Add totalPrice between filter based on the price bucket clicked
      const bucket = this.data?.summary?.priceBuckets?.find(b => b.label === name);
      if (bucket) {
        filters.push({
          field: 'totalPrice',
          operator: 'between',
          from: bucket.minCents,
          to: bucket.maxCents
        });
      }

      const serializedFilters = serializeFilters(filters);
      this.$router.push({
        path: '/tickets',
        query: { filters: serializedFilters }
      });
    },

    handleTopCounselorsBarClick(event) {
      const { name } = event;
      if (!name) return;

      // Find the counselor by name from the top counselors list
      const counselor = this.data?.summary?.topCounselors?.find(c => c.counselorName === name);

      const filters = [];

      // Add date range filter if available
      if (this.rangeSelection.startDate && this.rangeSelection.endDate) {
        filters.push({
          field: 'dateTicketStart',
          operator: 'between',
          from: this.rangeSelection.startDate,
          to: this.rangeSelection.endDate
        });
      }

      // Add productId filter
      if (this.selectedProductId) {
        filters.push({
          field: 'productIds',
          operator: 'any',
          values: [this.selectedProductId]
        });
      }

      // Add counselor filter if found
      if (counselor) {
        filters.push({
          field: 'counselorId',
          operator: 'is',
          value: counselor.counselorId
        });
      }

      const serializedFilters = serializeFilters(filters);
      this.$router.push({
        path: '/tickets',
        query: { filters: serializedFilters }
      });
    },

    navigateToTickets(startDate, endDate) {
      const filters = [
        {
          field: 'dateTicketStart',
          operator: 'between',
          from: startDate,
          to: endDate
        }
      ];

      // Add productId filter
      if (this.selectedProductId) {
        filters.push({
          field: 'productIds',
          operator: 'any',
          values: [this.selectedProductId]
        });
      }

      const serializedFilters = serializeFilters(filters);
      this.$router.push({
        path: '/tickets',
        query: { filters: serializedFilters }
      });
    },

    formatCurrencyValue(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value);
    },

    formatCurrencyShort(value) {
      if (value >= 1000000) {
        return '$' + (value / 1000000).toFixed(1) + 'M';
      } else if (value >= 1000) {
        return '$' + (value / 1000).toFixed(0) + 'K';
      }
      return '$' + value.toFixed(2);
    },

    async generatePDF() {
      const productName = this.data?.product?.productName || '';
      const title = productName ? `Product Details — ${productName}` : 'Product Details';
      this.$refs.baseReport.generateReportPDF({
        title,
        subtitle: this.dateRangeLabel,
        kpis: [
          { label: 'Total Tickets', value: this.totalTicketsKpi.value, format: 'number' },
          { label: 'Total Sales', value: this.totalSalesKpi.value, format: 'currency' },
          { label: 'Total Collected', value: this.totalCollectedKpi.value, format: 'currency' }
        ],
        columns: this.counselorSalesTableColumns,
        rows: this.counselorSalesTableRows,
        summaryRow: this.counselorSalesTableSummary
      });
    },

    getProductIcon(productType) {
      switch (productType) {
        case 'medication':
          return PillIcon;
        case 'fee':
          return CurrencyUsdIcon;
        case 'other':
        default:
          return PackageIcon;
      }
    }
  }
};
</script>

<style scoped>
.report-header-with-icon {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.report-header-text h2 {
  margin: 0 0 4px 0;
}

.report-header-text p {
  margin: 0;
}

.report-controls {
  display: flex;
  gap: 12px;
  align-items: center;
}

.product-select {
  width: 200px;
  flex-shrink: 0;
}

.empty-state {
  grid-column: 1 / -1;
  padding: 64px;
  text-align: center;
  color: #6b7280;
  font-size: 1rem;
}

.cell-chart {
  grid-column: span 2;
}

.cell-half {
  grid-column: span 3;
}

.cell-full-width {
  grid-column: span 6;
}

.cell-data-table :deep(.data-table th),
.cell-data-table :deep(.data-table td) {
  width: 200px;
  min-width: 200px;
  max-width: 200px;
}

@media (max-width: 768px) {
  .cell-chart,
  .cell-half,
  .cell-full-width {
    grid-column: span 6;
  }
}
</style>
